import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { api, firstFieldMessagesFromApiPayload, validationMessagesFromApiPayload } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { unwrapEntity } from '../../utils/apiPayload';
import {
  toISODate,
  fromISODate,
  stayNightsOverlapBlocked,
  isNightBlockedAsCheckIn,
  blockedIntervalsForPicker,
} from '../../utils/bookingCalendar';
import { paystackReturnReference } from '../../utils/paystackReturnReference';
import { formatLongDate } from '../../utils/formatLongDate';
import SEO from '../../components/SEO';
import Navbar from '../../component/Navbar/Navbar';
import Footer from '../../component/Footer/Footer';
import { PhoneWithCountryInput } from '../../components/PhoneWithCountryInput/PhoneWithCountryInput';
import './BookApartment.css';

/** Matches API default when marketing copy is not customised. */
const DEFAULT_CHECKOUT_REFUND_NOTICE =
  'Refund summary: Cancel at least 7 full calendar days before check-in and you receive a 100% refund of eligible amounts paid. Any other refund is an 80% of total amount. See our Terms for the full policy.';

function formatSurchargeFeeLabel(s) {
  if (!s) return '';
  if (s.billing_kind === 'fixed' && s.amount != null) {
    return `₦${Number(s.amount).toLocaleString()}`;
  }
  if (s.billing_kind === 'percent' && s.percent != null) {
    return `${s.percent}% of room`;
  }
  return '';
}

function surchargeWaivesGuestCapacity(s) {
  if (!s) return false;
  if (s.waives_guest_capacity) return true;
  const n = String(s.name || '').toLowerCase();
  const t = String(s.type_name || '').toLowerCase();
  return n.includes('party') || t.includes('party');
}

function scrollBookingValidationIntoView() {
  if (typeof document === 'undefined') return;
  requestAnimationFrame(() => {
    const root = document.querySelector('.book-form');
    if (!root) return;
    const target = root.querySelector('.book-field-error') || root.querySelector('.book-error');
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
}

/** Client-side checks so submit always reaches JS (form uses noValidate). */
function collectCheckoutClientErrors(f) {
  /** @type {Record<string, string>} */
  const e = {};
  if (!String(f.start_date || '').trim()) e.start_date = 'Please select your check-in date.';
  if (!String(f.end_date || '').trim()) e.end_date = 'Please select your check-out date.';
  const adults = Number(f.guests_adults);
  if (!Number.isFinite(adults) || adults < 1) e.guests_adults = 'Enter at least one adult.';
  const children = Number(f.guests_children);
  if (!Number.isFinite(children) || children < 0 || !Number.isInteger(children)) {
    e.guests_children = 'Enter a whole number of children (0 or more).';
  }
  if (!String(f.guest_name || '').trim()) e.guest_name = 'Please enter your full name.';
  const email = String(f.guest_email || '').trim();
  if (!email) e.guest_email = 'Please enter your email address.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.guest_email = 'Please enter a valid email address.';
  const phone = String(f.guest_phone || '').replace(/\s+/g, '');
  if (!phone) e.guest_phone = 'Please enter your phone number.';
  else if (!isValidPhoneNumber(phone)) {
    e.guest_phone =
      'Choose your country, then enter your mobile number without the leading 0 (e.g. 916 001 0451 for Nigeria).';
  }
  if (!f.payment_method) e.payment_method = 'Please choose how you will pay.';
  return e;
}

function RequiredMark() {
  return (
    <>
      <span className="book-label-required" aria-hidden="true">
        {' '}
        *
      </span>
      <span className="book-sr-only"> (required)</span>
    </>
  );
}

const BOOKING_INLINE_ERROR_KEYS = new Set([
  'start_date',
  'end_date',
  'guests_adults',
  'guests_children',
  'apartment_id',
  'guest_name',
  'guest_email',
  'guest_phone',
  'guest_id_document',
  'payment_method',
  'surcharge_ids',
]);

const BookApartment = () => {
  const { apartmentId } = useParams();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const [apartment, setApartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successTitle, setSuccessTitle] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [autoRegistered, setAutoRegistered] = useState(false);
  const [existingAccount, setExistingAccount] = useState(false);
  const [priceQuote, setPriceQuote] = useState(null);
  const [selectedSurchargeIds, setSelectedSurchargeIds] = useState([]);
  const prefilledDatesFromUrl = useRef(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [dateWarning, setDateWarning] = useState('');
  const [checkoutWarning, setCheckoutWarning] = useState('');
  const [checkoutRefundNotice, setCheckoutRefundNotice] = useState(DEFAULT_CHECKOUT_REFUND_NOTICE);
  const [form, setForm] = useState({
    start_date: '', end_date: '', guests_adults: 1, guests_children: 0,
    guest_name: '', guest_email: '', guest_phone: '', notes: '',
    payment_method: 'paystack',
  });
  const [guestIdFile, setGuestIdFile] = useState(null);

  const today = useMemo(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
  }, []);

  const minCheckoutDate = useMemo(() => {
    if (!form.start_date) return today;
    const s = fromISODate(form.start_date);
    if (!s) return today;
    return new Date(s.getFullYear(), s.getMonth(), s.getDate() + 1, 0, 0, 0);
  }, [form.start_date, today]);

  useEffect(() => {
    if (!prefilledDatesFromUrl.current) {
      const sd = searchParams.get('start_date');
      const ed = searchParams.get('end_date');
      if (sd && ed) {
        prefilledDatesFromUrl.current = true;
        setForm((f) => ({ ...f, start_date: sd, end_date: ed }));
      }
    }
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get('/public/marketing/site-content', null);
        const text =
          data && typeof data.checkout_refund_notice === 'string' && data.checkout_refund_notice.trim() !== ''
            ? data.checkout_refund_notice.trim()
            : DEFAULT_CHECKOUT_REFUND_NOTICE;
        if (!cancelled) setCheckoutRefundNotice(text);
      } catch {
        if (!cancelled) setCheckoutRefundNotice(DEFAULT_CHECKOUT_REFUND_NOTICE);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    api.get(`/apartments/${apartmentId}`, null)
      .then((res) => {
        const apt = unwrapEntity(res) || res.data || res;
        setApartment(apt);
        if (user) {
          setForm((f) => ({
            ...f,
            guest_name: f.guest_name || user.name || '',
            guest_email: f.guest_email || user.email || '',
            guest_phone: f.guest_phone || user.phone_number || '',
          }));
        }
      })
      .catch((err) => setError(err?.message || 'Could not load apartment'))
      .finally(() => setLoading(false));

    api.get(`/apartments/${apartmentId}/blocked-dates`, null)
      .then((res) => setBlockedDates(res.data || []))
      .catch(() => {});
  }, [apartmentId, user]);

  useEffect(() => {
    if (!apartmentId || !form.start_date || !form.end_date || form.end_date <= form.start_date) {
      setPriceQuote(null);
      return undefined;
    }
    let cancelled = false;
    const params = new URLSearchParams({
      start_date: form.start_date,
      end_date: form.end_date,
    });
    selectedSurchargeIds.forEach((id) => params.append('surcharge_ids[]', String(id)));
    const qs = `?${params.toString()}`;
    api
      .get(`/apartments/${apartmentId}${qs}`, null)
      .then((res) => {
        const apt = unwrapEntity(res) || res.data || res;
        if (!cancelled) setPriceQuote(apt?.price_quote || null);
      })
      .catch(() => {
        if (!cancelled) setPriceQuote(null);
      });
    return () => {
      cancelled = true;
    };
  }, [apartmentId, form.start_date, form.end_date, selectedSurchargeIds]);

  useEffect(() => {
    const available = priceQuote?.available_surcharges;
    if (!Array.isArray(available) || available.length === 0) {
      return;
    }
    const allowed = new Set(available.filter((s) => !s.is_mandatory).map((s) => s.id));
    setSelectedSurchargeIds((prev) => prev.filter((id) => allowed.has(id)));
  }, [priceQuote]);

  const checkBlockedOverlap = useCallback((startDate, endDate) => {
    if (!startDate || !endDate || endDate <= startDate) {
      setDateWarning('');
      return false;
    }
    if (blockedDates.length === 0) {
      setDateWarning('');
      return false;
    }
    if (stayNightsOverlapBlocked(startDate, endDate, blockedDates)) {
      setDateWarning('Some selected nights overlap a booked or blocked period. Please choose different dates.');
      return true;
    }
    setDateWarning('');
    return false;
  }, [blockedDates]);

  const isDateBlocked = useCallback(
    (date) => isNightBlockedAsCheckIn(date, blockedDates, today),
    [blockedDates, today],
  );

  const unavailableIntervals = useMemo(() => blockedIntervalsForPicker(blockedDates), [blockedDates]);

  const canSelectStartDate = useCallback(
    (date) => {
      const dateAtMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
      return dateAtMidnight >= today && !isNightBlockedAsCheckIn(date, blockedDates, today);
    },
    [today, blockedDates],
  );

  const canSelectEndDate = useCallback(
    (date) => {
      if (!form.start_date) return false;
      const start = fromISODate(form.start_date);
      const d = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
      if (!start || d <= start) return false;
      const checkoutISO = toISODate(date);
      return !stayNightsOverlapBlocked(form.start_date, checkoutISO, blockedDates);
    },
    [form.start_date, blockedDates],
  );

  const renderDayContents = useCallback((dayOfMonth, date) => {
    if (!date || !isDateBlocked(date)) return dayOfMonth;
    return (
      <span className="book-day-content book-day-content-blocked">
        <span>{dayOfMonth}</span>
        <span className="book-day-slash" aria-hidden="true" />
      </span>
    );
  }, [isDateBlocked]);

  useEffect(() => {
    if (form.start_date && form.end_date && form.end_date <= form.start_date) {
      setCheckoutWarning('Check-out date must be after check-in date.');
    } else {
      setCheckoutWarning('');
    }
  }, [form.start_date, form.end_date]);

  const fallbackEstimate = useMemo(() => {
    if (!apartment?.price || !form.start_date || !form.end_date) return 0;
    const start = new Date(form.start_date);
    const end = new Date(form.end_date);
    const nights = Math.max(0, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
    return nights * Number(apartment.price);
  }, [apartment, form.start_date, form.end_date]);

  const displayTotal = useMemo(() => {
    if (priceQuote && priceQuote.total != null && Number(priceQuote.total) >= 0) {
      return Number(priceQuote.total);
    }
    return fallbackEstimate;
  }, [priceQuote, fallbackEstimate]);

  const displaySubtotal = priceQuote?.subtotal != null ? Number(priceQuote.subtotal) : null;
  const stayAfterDiscount =
    priceQuote?.stay_total_after_discount != null
      ? Number(priceQuote.stay_total_after_discount)
      : null;
  const surchargeTotal =
    priceQuote?.surcharge_total != null ? Number(priceQuote.surcharge_total) : 0;
  const hasStayDiscount =
    Boolean(priceQuote?.applied_discount) &&
    displaySubtotal != null &&
    stayAfterDiscount != null &&
    displaySubtotal > stayAfterDiscount;

  const mandatorySurcharges = useMemo(
    () =>
      Array.isArray(priceQuote?.available_surcharges)
        ? priceQuote.available_surcharges.filter((s) => s.is_mandatory)
        : [],
    [priceQuote],
  );
  const optionalSurcharges = useMemo(
    () =>
      Array.isArray(priceQuote?.available_surcharges)
        ? priceQuote.available_surcharges.filter((s) => !s.is_mandatory)
        : [],
    [priceQuote],
  );

  const guestCapacityWaivedByPartyFee = useMemo(() => {
    const list = priceQuote?.available_surcharges;
    if (!Array.isArray(list)) return false;
    return selectedSurchargeIds.some((id) => {
      const s = list.find((x) => Number(x.id) === Number(id));
      return Boolean(s && surchargeWaivesGuestCapacity(s));
    });
  }, [priceQuote, selectedSurchargeIds]);

  const maxAdultsForForm = guestCapacityWaivedByPartyFee
    ? 50
    : Math.max(1, Number(apartment?.capacity_adults ?? 20));
  const maxChildrenForForm = guestCapacityWaivedByPartyFee
    ? 50
    : Math.max(0, Number(apartment?.capacity_children ?? 20));

  const toggleSurcharge = useCallback((id) => {
    setFieldErrors((prev) => {
      if (!prev.surcharge_ids) return prev;
      const next = { ...prev };
      delete next.surcharge_ids;
      return next;
    });
    setSelectedSurchargeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const nightCount = useMemo(() => {
    if (!form.start_date || !form.end_date || form.end_date <= form.start_date) return 0;
    const s = fromISODate(form.start_date);
    const e = fromISODate(form.end_date);
    if (!s || !e) return 0;
    return Math.max(0, Math.round((e.getTime() - s.getTime()) / 86400000));
  }, [form.start_date, form.end_date]);

  const indicativeNightly =
    nightCount > 0 && apartment?.price != null ? Number(apartment.price) : null;

  useEffect(() => {
    checkBlockedOverlap(form.start_date, form.end_date);
  }, [form.start_date, form.end_date, checkBlockedOverlap]);

  const verifyPaystackPayment = useCallback(async (reference) => {
    setVerifying(true);
    try {
      const res = await api.post('/bookings/verify-payment', { reference }, null);
      setSuccess(true);
      setSuccessTitle('Booking Confirmed!');
      setSuccessMessage(res.message || 'Payment verified and booking confirmed!');
    } catch (err) {
      setError(err?.message || 'Payment verification failed. Please contact support.');
    } finally {
      setVerifying(false);
    }
  }, []);

  useEffect(() => {
    const verifyRef = paystackReturnReference(searchParams);
    if (verifyRef && !success) {
      verifyPaystackPayment(verifyRef);
    }
  }, [searchParams, success, verifyPaystackPayment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name) {
      setFieldErrors((prev) => {
        if (!prev[name]) return prev;
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    setForm((f) => ({ ...f, [name]: value }));
    if (error) setError(null);
  };

  const handleStartDateChange = (date) => {
    if (date && isNightBlockedAsCheckIn(date, blockedDates, today)) {
      setDateWarning('That check-in date is unavailable (booked/blocked). Please choose another date.');
      return;
    }
    setDateWarning('');
    const start = toISODate(date);
    setForm((prev) => {
      const next = { ...prev, start_date: start };
      if (next.end_date && next.end_date <= start) {
        next.end_date = '';
      }
      return next;
    });
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.start_date;
      delete next.end_date;
      return next;
    });
    if (error) setError(null);
  };

  const handleEndDateChange = (date) => {
    if (!date || !form.start_date) {
      setForm((prev) => ({ ...prev, end_date: date ? toISODate(date) : '' }));
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.end_date;
        return next;
      });
      if (error) setError(null);
      return;
    }
    const checkoutISO = toISODate(date);
    if (stayNightsOverlapBlocked(form.start_date, checkoutISO, blockedDates)) {
      setDateWarning('That stay overlaps a booked or blocked period. Please choose a later check-out or different check-in.');
      return;
    }
    setDateWarning('');
    setForm((prev) => ({ ...prev, end_date: checkoutISO }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.end_date;
      return next;
    });
    if (error) setError(null);
  };

  const loadPaystackScript = () => {
    return new Promise((resolve) => {
      if (window.PaystackPop) {
        resolve(window.PaystackPop);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://js.paystack.co/v2/inline.js';
      script.onload = () => resolve(window.PaystackPop);
      document.head.appendChild(script);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (dateWarning || checkoutWarning) return;
    setError(null);
    setFieldErrors({});

    const clientFe = collectCheckoutClientErrors(form);
    if (Object.keys(clientFe).length > 0) {
      setFieldErrors(clientFe);
      setError(Object.values(clientFe));
      scrollBookingValidationIntoView();
      return;
    }

    setSubmitting(true);
    setExistingAccount(false);
    try {
      const baseFields = {
        apartment_id: Number(apartmentId),
        start_date: form.start_date,
        end_date: form.end_date,
        guests_adults: Number(form.guests_adults),
        guests_children: Number(form.guests_children),
        guest_name: form.guest_name,
        guest_email: form.guest_email,
        guest_phone: form.guest_phone,
        notes: form.notes,
        payment_method: form.payment_method,
        ...(selectedSurchargeIds.length > 0 ? { surcharge_ids: selectedSurchargeIds } : {}),
      };

      let res;
      if (guestIdFile) {
        const fd = new FormData();
        Object.entries(baseFields).forEach(([k, v]) => {
          if (k === 'surcharge_ids') return;
          fd.append(k, String(v));
        });
        selectedSurchargeIds.forEach((id) => fd.append('surcharge_ids[]', String(id)));
        fd.append('guest_id_document', guestIdFile);
        res = await api.post('/bookings', fd);
      } else {
        res = await api.post('/bookings', baseFields);
      }
      const booking = unwrapEntity(res) || res.data;

      if (res.access_token) {
        localStorage.setItem('luxistt_token', res.access_token);
        await refreshUser();
      }
      if (res.auto_registered) {
        setAutoRegistered(true);
      } else {
        setAutoRegistered(false);
      }
      if (res.existing_account) {
        setExistingAccount(true);
        if (booking?.guest_name) {
          setForm((f) => ({
            ...f,
            guest_name: booking.guest_name,
            guest_email: booking.guest_email || f.guest_email,
            guest_phone: booking.guest_phone || f.guest_phone,
          }));
        }
      }

      if (form.payment_method === 'paystack' && res.payment) {
        if (res.payment.error) {
          setError(res.payment.error);
          return;
        }
        if (res.payment.authorization_url) {
          window.location.href = res.payment.authorization_url;
          return;
        }

        try {
          const PaystackPop = await loadPaystackScript();
          const paystackKeyRes = await api.get('/paystack/key', null);
          if (!res.payment?.reference) {
            throw new Error(res.payment?.error || res.message || 'Could not initialize online payment.');
          }
          const popup = new PaystackPop();
          popup.newTransaction({
            key: paystackKeyRes.public_key,
            email: booking?.guest_email || form.guest_email,
            amount: (booking?.total_price ?? displayTotal) * 100,
            reference: res.payment.reference,
            onSuccess: (transaction) => {
              verifyPaystackPayment(transaction.reference);
            },
            onCancel: () => {
              setError('Payment was cancelled. If a booking was created, you can complete payment from My Bookings.');
              setSubmitting(false);
            },
          });
        } catch {
          setError(res.payment?.error || res.message || 'Booking created, but payment could not be initialized. Please continue from My Bookings.');
          setSubmitting(false);
        }
        return;
      }

      if (form.payment_method === 'paystack' && !res.payment) {
        setError(res.message || 'Booking created, but payment was not initialized. Please continue from My Bookings.');
        return;
      }

      setSuccess(true);
      if (form.payment_method === 'cash') {
        setSuccessTitle('Booking Submitted!');
        setSuccessMessage('Booking submitted! Our team will confirm your booking once payment is received.');
      } else {
        setSuccessTitle('Booking Confirmed!');
        setSuccessMessage('Booking confirmed.');
      }
    } catch (err) {
      if (err?.status === 422 && err?.data?.access_token) {
        localStorage.setItem('luxistt_token', err.data.access_token);
        await refreshUser();
      }
      if (err?.status === 422 && err?.data?.payment?.error) {
        setFieldErrors({});
        setError(err.data.payment.error);
        scrollBookingValidationIntoView();
        return;
      }
      const fe = firstFieldMessagesFromApiPayload(err?.data);
      setFieldErrors(fe);
      const msgs = validationMessagesFromApiPayload(err?.data);
      setError(
        msgs.length > 0 ? msgs : [err?.message || 'Booking failed. Please try again.'],
      );
      scrollBookingValidationIntoView();
    } finally {
      setSubmitting(false);
    }
  };

  const isSubmitDisabled = submitting || !!dateWarning || !!checkoutWarning;

  const extraFieldErrorEntries = useMemo(
    () => Object.entries(fieldErrors).filter(([k]) => !BOOKING_INLINE_ERROR_KEYS.has(k)),
    [fieldErrors],
  );

  const submitButtonLabel = submitting
    ? 'Processing…'
    : form.payment_method === 'paystack'
      ? 'Proceed to Payment'
      : 'Submit Booking';
  const showMobileCheckoutBar = displayTotal > 0;

  if (loading || verifying) {
    return (
      <div className="book-page">
        <Navbar />
        <div className="book-content">
          <div className="book-skeleton-grid" aria-busy="true" aria-live="polite">
            <div className="book-skeleton-card">
              <div className="book-skeleton-line book-skeleton-line-lg shimmer" />
              <div className="book-skeleton-line book-skeleton-line-md shimmer" />
              <div className="book-skeleton-line book-skeleton-line-sm shimmer" />
            </div>
            <div className="book-skeleton-card">
              <div className="book-skeleton-line book-skeleton-line-lg shimmer" />
              <div className="book-skeleton-line book-skeleton-line-md shimmer" />
              <div className="book-skeleton-line book-skeleton-line-full shimmer" />
              <div className="book-skeleton-line book-skeleton-line-full shimmer" />
            </div>
          </div>
          <div className="book-loading">{verifying ? 'Verifying payment…' : 'Loading booking page…'}</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (success) {
    return (
      <div className="book-page">
        <Navbar />
        <div className="book-success">
          <div className="book-success-icon">✓</div>
          <h2>{successTitle || (form.payment_method === 'cash' ? 'Booking Submitted!' : 'Booking Confirmed!')}</h2>
          <p>{successMessage || 'A confirmation email has been sent.'}</p>
          {autoRegistered && (
            <div className="book-auto-registered">
              <p>We&apos;ve created an account for <strong>{form.guest_email}</strong>. Check your email to set your password and manage your bookings.</p>
            </div>
          )}
          {existingAccount && !autoRegistered && (
            <div className="book-existing-account">
              <p>
                This booking is linked to your existing Luxistt account. Guest details were taken from your profile. You can manage it anytime under <strong>My Bookings</strong>.
              </p>
            </div>
          )}
          <div className="book-success-actions">
            <Link to="/rooms" className="btn-primary">Browse more apartments</Link>
            <Link to="/my-bookings" className="btn-ghost">My Bookings</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const property = apartment?.property || {};

  return (
    <div className={`book-page${showMobileCheckoutBar ? ' book-page--mobile-bar' : ''}`}>
      <SEO
        title={apartment ? `Book ${apartment.name}` : 'Book a stay'}
        description={apartment ? `Book ${apartment.name} — affordable short stay in Nigeria. Select dates, pay securely, and check in.` : 'Book a short stay with Luxistt.'}
        path={`/book/${apartmentId}`}
        noindex
      />
      <Navbar />
      <div className="book-content">
        <header className="book-page-header">
          <h1 className="book-title">Book your short stay</h1>
          <p className="book-subtitle">Choose dates, add guest details, then pay securely.</p>
        </header>

        <div className="book-grid">
          <aside className="book-summary-aside">
            <div className="book-apartment-summary glass-panel book-summary-card">
            <h2>{apartment?.name}</h2>
            <p className="book-property-name">{property.name}</p>
            {property.address && <p className="book-address">{property.address}</p>}
            <div className="book-apt-meta">
              {apartment?.type && <span>{apartment.type}</span>}
              {apartment?.size && <span>{apartment.size} m²</span>}
              {apartment?.bathrooms != null && <span>{apartment.bathrooms} Bathroom(s)</span>}
            </div>
            {apartment?.price != null && (
              <div className="book-apt-price">₦{Number(apartment.price).toLocaleString()} <span>/ night</span></div>
            )}
            {nightCount > 0 && (
              <p className="book-nights-line">
                {nightCount} night{nightCount === 1 ? '' : 's'}
                {indicativeNightly ? ` · from ₦${indicativeNightly.toLocaleString()} / night (indicative)` : ''}
              </p>
            )}
            {displayTotal > 0 && (
              <div className="book-estimated-total book-summary-total-block">
                {hasStayDiscount && displaySubtotal != null ? (
                  <>
                    <div className="book-total-row">
                      <span className="book-total-label">Subtotal</span>
                      <span className="book-total-strike">₦{displaySubtotal.toLocaleString()}</span>
                    </div>
                    {stayAfterDiscount != null && surchargeTotal > 0 ? (
                      <div className="book-total-row">
                        <span className="book-total-label">Stay (after discount)</span>
                        <span className="book-total-value">₦{stayAfterDiscount.toLocaleString()}</span>
                      </div>
                    ) : null}
                    {Array.isArray(priceQuote?.surcharge_lines) &&
                      priceQuote.surcharge_lines.map((line) => (
                        <div key={line.id} className="book-total-row book-total-row-surcharge">
                          <span className="book-total-label">{line.name}</span>
                          <span className="book-total-value">₦{Number(line.amount).toLocaleString()}</span>
                        </div>
                      ))}
                    <div className="book-total-row book-total-row-final">
                      <span className="book-total-label">Total amount to pay</span>
                      <span className="book-total-value">₦{displayTotal.toLocaleString()}</span>
                    </div>
                    {priceQuote?.applied_discount?.name && (
                      <p className="book-discount-note">
                        {priceQuote.applied_discount.name} · {Number(priceQuote.applied_discount.percent)}% off
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    {Array.isArray(priceQuote?.surcharge_lines) &&
                      priceQuote.surcharge_lines.map((line) => (
                        <div key={line.id} className="book-total-row book-total-row-surcharge">
                          <span className="book-total-label">{line.name}</span>
                          <span className="book-total-value">₦{Number(line.amount).toLocaleString()}</span>
                        </div>
                      ))}
                    <div className="book-total-row book-total-row-final">
                      <span className="book-total-label">Total amount to pay</span>
                      <span className="book-total-value">₦{displayTotal.toLocaleString()}</span>
                    </div>
                    {nightCount > 0 && (
                      <p className="book-total-breakdown">
                        {nightCount} night{nightCount === 1 ? '' : 's'} · avg ₦{Math.round(displayTotal / nightCount).toLocaleString()} / night
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
            </div>
          </aside>

          <form
            id="book-checkout-form"
            className={`book-form glass-panel book-form-panel ${submitting ? 'book-form-submitting' : ''}`}
            onSubmit={handleSubmit}
            aria-busy={submitting}
            noValidate
          >
            {error && (
              <div className="book-error" role="alert" id="book-checkout-error-summary">
                {Array.isArray(error) ? (
                  <ul className="book-error-list">
                    {error.map((msg, i) => (
                      <li key={i}>{typeof msg === 'string' ? msg : JSON.stringify(msg)}</li>
                    ))}
                  </ul>
                ) : (
                  <p>{error}</p>
                )}
              </div>
            )}

            <div className="book-form-row">
              <div className="book-form-field">
                <label htmlFor="start_date">
                  Check-in
                  <RequiredMark />
                </label>
                <DatePicker
                  id="start_date"
                  selected={fromISODate(form.start_date)}
                  onChange={handleStartDateChange}
                  onKeyDown={(e) => e.preventDefault()}
                  minDate={today}
                  filterDate={canSelectStartDate}
                  excludeDateIntervals={unavailableIntervals}
                  dayClassName={(date) => (isDateBlocked(date) ? 'book-day-blocked' : undefined)}
                  renderDayContents={renderDayContents}
                  placeholderText="Select check-in date"
                  dateFormat="d MMMM yyyy"
                  className={`book-datepicker-input${fieldErrors.start_date ? ' book-input-invalid' : ''}`}
                  aria-invalid={fieldErrors.start_date ? true : undefined}
                  aria-describedby={fieldErrors.start_date ? 'book-err-start_date' : undefined}
                />
                {fieldErrors.start_date ? (
                  <p id="book-err-start_date" className="book-field-error" role="alert">
                    {fieldErrors.start_date}
                  </p>
                ) : null}
                {form.start_date ? (
                  <span className="book-date-long">{formatLongDate(form.start_date)}</span>
                ) : null}
              </div>
              <div className="book-form-field">
                <label htmlFor="end_date">
                  Check-out
                  <RequiredMark />
                </label>
                <DatePicker
                  id="end_date"
                  selected={fromISODate(form.end_date)}
                  onChange={handleEndDateChange}
                  onKeyDown={(e) => e.preventDefault()}
                  minDate={minCheckoutDate}
                  filterDate={canSelectEndDate}
                  excludeDateIntervals={unavailableIntervals}
                  dayClassName={(date) => (isDateBlocked(date) ? 'book-day-blocked' : undefined)}
                  renderDayContents={renderDayContents}
                  placeholderText="Select check-out date"
                  dateFormat="d MMMM yyyy"
                  disabled={!form.start_date}
                  className={`book-datepicker-input${fieldErrors.end_date ? ' book-input-invalid' : ''}`}
                  aria-invalid={fieldErrors.end_date ? true : undefined}
                  aria-describedby={fieldErrors.end_date ? 'book-err-end_date' : undefined}
                />
                {fieldErrors.end_date ? (
                  <p id="book-err-end_date" className="book-field-error" role="alert">
                    {fieldErrors.end_date}
                  </p>
                ) : null}
                {form.end_date ? (
                  <span className="book-date-long">{formatLongDate(form.end_date)}</span>
                ) : null}
              </div>
            </div>

            {dateWarning && (
              <div className="book-date-warning" role="alert">{dateWarning}</div>
            )}
            {checkoutWarning && (
              <div className="book-date-warning" role="alert">{checkoutWarning}</div>
            )}

            {mandatorySurcharges.length > 0 || optionalSurcharges.length > 0 ? (
              <div className="book-form-field book-surcharges-field">
                {fieldErrors.surcharge_ids ? (
                  <p id="book-err-surcharge_ids" className="book-field-error" role="alert">
                    {fieldErrors.surcharge_ids}
                  </p>
                ) : null}
                {mandatorySurcharges.length > 0 ? (
                  <div className="party-surcharge-mandatory-block">
                    <p className="party-surcharge-section-title">Included with this stay</p>
                    <ul className="party-surcharge-included-list">
                      {mandatorySurcharges.map((s) => (
                        <li key={s.id} className="party-surcharge-included-row">
                          <div className="party-surcharge-included-copy">
                            <span className="party-surcharge-main">{s.name}</span>
                            {s.type_name ? <span className="party-surcharge-type">{s.type_name}</span> : null}
                            {s.description ? <span className="party-surcharge-desc">{s.description}</span> : null}
                          </div>
                          <span className="party-surcharge-fee-pill">{formatSurchargeFeeLabel(s)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {optionalSurcharges.length > 0 ? (
                  <>
                    {optionalSurcharges.length > 1 ? (
                      <p className="party-surcharge-intro book-party-surcharge-intro">
                        Optional add-ons — select any that apply.
                      </p>
                    ) : null}
                    {optionalSurcharges.length === 1 ? (
                      (() => {
                        const s = optionalSurcharges[0];
                        const fee = formatSurchargeFeeLabel(s);
                        return (
                          <label className="party-surcharge-row party-surcharge-row--single">
                            <input
                              type="checkbox"
                              className="party-surcharge-checkbox"
                              checked={selectedSurchargeIds.includes(s.id)}
                              onChange={() => toggleSurcharge(s.id)}
                            />
                            <span className="party-surcharge-label party-surcharge-label--party-option">
                              <span className="party-surcharge-main">Tick if booking for a party.</span>
                              {fee ? (
                                <span className="party-surcharge-fee-note party-surcharge-fee-note--party">
                                  Adds an extra <strong>{fee}</strong> to your booking total when ticked.
                                </span>
                              ) : null}
                            </span>
                          </label>
                        );
                      })()
                    ) : (
                      <ul className="party-surcharge-multi-list">
                        {optionalSurcharges.map((s) => (
                          <li key={s.id}>
                            <label className="party-surcharge-row party-surcharge-row--multi">
                              <input
                                type="checkbox"
                                className="party-surcharge-checkbox"
                                checked={selectedSurchargeIds.includes(s.id)}
                                onChange={() => toggleSurcharge(s.id)}
                              />
                              <span className="party-surcharge-label">
                                <span className="party-surcharge-main">{s.name}</span>
                                {s.type_name ? (
                                  <span className="party-surcharge-type">{s.type_name}</span>
                                ) : null}
                                {s.description ? (
                                  <span className="party-surcharge-desc">{s.description}</span>
                                ) : null}
                              </span>
                              <span className="party-surcharge-fee-pill">{formatSurchargeFeeLabel(s)}</span>
                            </label>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : null}
              </div>
            ) : null}

            <div className="book-form-row">
              <div className="book-form-field">
                <label htmlFor="guests_adults">
                  Adults
                  <RequiredMark />
                </label>
                <input
                  type="number"
                  id="guests_adults"
                  name="guests_adults"
                  value={form.guests_adults}
                  onChange={handleChange}
                  min={1}
                  max={maxAdultsForForm}
                  className={fieldErrors.guests_adults ? 'book-input-invalid' : undefined}
                  aria-invalid={fieldErrors.guests_adults ? true : undefined}
                  aria-describedby={
                    fieldErrors.guests_adults ? 'book-err-guests_adults' : undefined
                  }
                />
                {fieldErrors.guests_adults ? (
                  <p id="book-err-guests_adults" className="book-field-error" role="alert">
                    {fieldErrors.guests_adults}
                  </p>
                ) : null}
              </div>
              <div className="book-form-field">
                <label htmlFor="guests_children">Children</label>
                <input
                  type="number"
                  id="guests_children"
                  name="guests_children"
                  value={form.guests_children}
                  onChange={handleChange}
                  min={0}
                  max={maxChildrenForForm}
                  className={fieldErrors.guests_children ? 'book-input-invalid' : undefined}
                  aria-invalid={fieldErrors.guests_children ? true : undefined}
                  aria-describedby={
                    fieldErrors.guests_children ? 'book-err-guests_children' : undefined
                  }
                />
                {fieldErrors.guests_children ? (
                  <p id="book-err-guests_children" className="book-field-error" role="alert">
                    {fieldErrors.guests_children}
                  </p>
                ) : null}
              </div>
            </div>
            {fieldErrors.apartment_id ? (
              <p id="book-err-apartment_id" className="book-field-error book-field-error--span" role="alert">
                {fieldErrors.apartment_id}
              </p>
            ) : null}

            <h3 className="book-form-section-title">Your Details</h3>

            <div className="book-form-field">
              <label htmlFor="guest_name">
                Full name
                <RequiredMark />
              </label>
              <input
                type="text"
                id="guest_name"
                name="guest_name"
                value={form.guest_name}
                onChange={handleChange}
                placeholder="John Doe"
                autoComplete="name"
                className={fieldErrors.guest_name ? 'book-input-invalid' : undefined}
                aria-invalid={fieldErrors.guest_name ? true : undefined}
                aria-describedby={fieldErrors.guest_name ? 'book-err-guest_name' : undefined}
              />
              {fieldErrors.guest_name ? (
                <p id="book-err-guest_name" className="book-field-error" role="alert">
                  {fieldErrors.guest_name}
                </p>
              ) : null}
            </div>
            <div className="book-form-row">
              <div className="book-form-field">
                <label htmlFor="guest_email">
                  Email
                  <RequiredMark />
                </label>
                <input
                  type="email"
                  id="guest_email"
                  name="guest_email"
                  value={form.guest_email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  autoComplete="email"
                  className={fieldErrors.guest_email ? 'book-input-invalid' : undefined}
                  aria-invalid={fieldErrors.guest_email ? true : undefined}
                  aria-describedby={fieldErrors.guest_email ? 'book-err-guest_email' : undefined}
                />
                {fieldErrors.guest_email ? (
                  <p id="book-err-guest_email" className="book-field-error" role="alert">
                    {fieldErrors.guest_email}
                  </p>
                ) : null}
              </div>
              <div className="book-form-field">
                <label htmlFor="guest_phone">
                  Phone
                  <RequiredMark />
                </label>
                <p id="book-hint-guest_phone" className="book-field-hint" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
                  Use the country flag for the dial code, then your number without the leading 0 (e.g. 916 001 0451 for +234).
                </p>
                <PhoneWithCountryInput
                  id="guest_phone"
                  name="guest_phone"
                  value={form.guest_phone}
                  onChange={(v) => {
                    setForm((f) => ({ ...f, guest_phone: v }));
                    setFieldErrors((prev) => {
                      if (!prev.guest_phone) return prev;
                      const next = { ...prev };
                      delete next.guest_phone;
                      return next;
                    });
                    if (error) setError(null);
                  }}
                  variant="book"
                  hasError={!!fieldErrors.guest_phone}
                  autoComplete="tel"
                  aria-invalid={fieldErrors.guest_phone ? true : undefined}
                  aria-describedby={
                    fieldErrors.guest_phone ? 'book-err-guest_phone book-hint-guest_phone' : 'book-hint-guest_phone'
                  }
                />
                {fieldErrors.guest_phone ? (
                  <p id="book-err-guest_phone" className="book-field-error" role="alert">
                    {fieldErrors.guest_phone}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="book-form-field">
              <label htmlFor="notes">Special Requests (optional)</label>
              <textarea id="notes" name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="Early check-in, extra pillows, etc." />
            </div>

            <h3 className="book-form-section-title">ID photo (optional)</h3>
            <p className="book-field-hint" style={{ marginTop: 0 }}>
              Snap a clear photo of your government ID or passport. Accepted: JPG, PNG, WebP (max 10MB).
            </p>
            <div className="book-form-field book-guest-id-camera">
              <label htmlFor="guest_id_document">Upload</label>
              <div className="book-guest-id-actions">
                <input
                  id="guest_id_document"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  capture="environment"
                  className={`book-guest-id-input${fieldErrors.guest_id_document ? ' book-input-invalid' : ''}`}
                  aria-invalid={fieldErrors.guest_id_document ? true : undefined}
                  aria-describedby={
                    fieldErrors.guest_id_document ? 'book-err-guest_id_document' : undefined
                  }
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    setGuestIdFile(f || null);
                    setFieldErrors((prev) => {
                      if (!prev.guest_id_document) return prev;
                      const next = { ...prev };
                      delete next.guest_id_document;
                      return next;
                    });
                    if (error) setError(null);
                  }}
                />
                {guestIdFile ? (
                  <span className="book-guest-id-name">{guestIdFile.name}</span>
                ) : null}
              </div>
              {fieldErrors.guest_id_document ? (
                <p id="book-err-guest_id_document" className="book-field-error" role="alert">
                  {fieldErrors.guest_id_document}
                </p>
              ) : null}
            </div>

            <h3 className="book-form-section-title">Payment method</h3>
            {fieldErrors.payment_method ? (
              <p id="book-err-payment_method" className="book-field-error" role="alert">
                {fieldErrors.payment_method}
              </p>
            ) : null}
            <div className="book-payment-options">
              <label className={`book-payment-option ${form.payment_method === 'paystack' ? 'selected' : ''}`}>
                <input type="radio" name="payment_method" value="paystack" checked={form.payment_method === 'paystack'} onChange={handleChange} />
                <div className="book-payment-option-content">
                  <span className="book-payment-icon">💳</span>
                  <div>
                    <strong>Pay Online (Paystack)</strong>
                    <small>Card, Bank Transfer, USSD — instant confirmation</small>
                  </div>
                </div>
              </label>
              <label className={`book-payment-option ${form.payment_method === 'cash' ? 'selected' : ''}`}>
                <input type="radio" name="payment_method" value="cash" checked={form.payment_method === 'cash'} onChange={handleChange} />
                <div className="book-payment-option-content">
                  <span className="book-payment-icon">💵</span>
                  <div>
                    <strong>Pay with Cash</strong>
                    <small>Pay on arrival — booking confirmed after admin approval</small>
                  </div>
                </div>
              </label>
            </div>

            <div className="book-refund-notice" role="region" aria-labelledby="book-refund-notice-title">
              <h3 id="book-refund-notice-title" className="book-refund-notice-title">
                Refunds &amp; cancellations
              </h3>
              <p className="book-refund-notice-body">{checkoutRefundNotice}</p>
              <p className="book-refund-notice-foot">
                <Link to="/terms">Read full terms</Link>
              </p>
            </div>

            {displayTotal > 0 && (
              <div className="book-total-summary book-total-summary--sidebar-redundant">
                <div className="book-total-summary-label">Total amount to pay</div>
                {hasStayDiscount && displaySubtotal != null ? (
                  <>
                    <span className="book-total-strike">₦{displaySubtotal.toLocaleString()}</span>
                    <span className="book-total-summary-final">₦{displayTotal.toLocaleString()}</span>
                  </>
                ) : (
                  <>
                    <span className="book-total-summary-final">₦{displayTotal.toLocaleString()}</span>
                    {nightCount > 0 ? (
                      <span className="book-total-summary-meta">
                        {nightCount} night{nightCount === 1 ? '' : 's'} · calculated from nightly rates for your dates
                      </span>
                    ) : null}
                  </>
                )}
              </div>
            )}

            {extraFieldErrorEntries.length > 0 ? (
              <div className="book-field-error-extra" role="alert">
                {extraFieldErrorEntries.map(([key, msg]) => (
                  <p key={key} className="book-field-error book-field-error--span">
                    <span className="book-field-error-key">{key.replace(/_/g, ' ')}:</span> {msg}
                  </p>
                ))}
              </div>
            ) : null}

            <button
              type="submit"
              className={`btn-primary book-submit${showMobileCheckoutBar ? ' book-submit--hidden-mobile' : ''}`}
              disabled={isSubmitDisabled}
            >
              {submitButtonLabel}
            </button>
            {submitting && (
              <p className="book-disclaimer">Preparing your booking and payment session…</p>
            )}
            {form.payment_method === 'cash' && (
              <p className="book-disclaimer">Your booking will be pending until approved by our team. You'll receive an email once confirmed.</p>
            )}
            {form.payment_method === 'paystack' && (
              <p className="book-disclaimer">You'll be redirected to Paystack to complete payment securely. Booking is confirmed instantly after payment.</p>
            )}
            {!user && (
              <p className="book-disclaimer book-disclaimer-account">
                No account needed — if your email is new we&apos;ll create one and email you; if you already have a Luxistt account we&apos;ll attach this booking to it using your saved details.
              </p>
            )}
          </form>
        </div>
      </div>

      {showMobileCheckoutBar ? (
        <div className="book-mobile-checkout" role="region" aria-label="Booking total and checkout">
          <div className="book-mobile-checkout-inner">
            <div className="book-mobile-checkout-total">
              <span className="book-mobile-checkout-label">Total to pay</span>
              <div className="book-mobile-checkout-amount-row">
                {hasStayDiscount && displaySubtotal != null ? (
                  <>
                    <span className="book-mobile-checkout-was">₦{displaySubtotal.toLocaleString()}</span>
                    <span className="book-mobile-checkout-amount">₦{displayTotal.toLocaleString()}</span>
                  </>
                ) : (
                  <span className="book-mobile-checkout-amount">₦{displayTotal.toLocaleString()}</span>
                )}
              </div>
              {nightCount > 0 ? (
                <span className="book-mobile-checkout-meta">
                  {nightCount} night{nightCount === 1 ? '' : 's'}
                  {priceQuote?.applied_discount?.name
                    ? ` · ${priceQuote.applied_discount.name}`
                    : ''}
                </span>
              ) : null}
            </div>
            <button
              type="submit"
              form="book-checkout-form"
              className="btn-primary book-mobile-checkout-cta"
              disabled={isSubmitDisabled}
            >
              {submitButtonLabel}
            </button>
          </div>
        </div>
      ) : null}

      <Footer />
    </div>
  );
};

export default BookApartment;
